import React, { lazy, Suspense, useEffect, useLayoutEffect, useRef } from "react";
import { BrowserRouter, Navigate, Routes, Route, useLocation, useNavigate, useNavigationType, useParams } from "react-router-dom";

import { LanguageContext, LanguageProvider } from "./contexts/LanguageContext";
import { DIGITAL_SOUVENIR_NON_DEFAULT_LANGUAGE_CODES } from "./seo/multilingualDigitalSouvenirs";
import { LOCALIZED_INFORMATIONAL_ROUTE_DEFINITIONS } from "./seo/multilingualInformationalPages";
import {
  NON_DEFAULT_SITE_LANGUAGE_CODES,
  inferSiteLanguageCode,
} from "./seo/localizedSiteRoutes";
import { LOCALIZED_SEO_LANDING_ROUTE_DEFINITIONS } from "./seo/multilingualSeoLandingPages";
import { bindStoreLinkTracking, initAnalytics, trackPageView } from "./utils/analytics";
import CookieConsentBanner from "./component/sections/consentBanner/CookieConsentBanner";
import { captureAttributionFromUrl } from "./utils/attribution";
import { loadWithChunkRecovery } from "./utils/chunkRecovery";
import { decorateStoreLinks } from "./utils/storeLinks";
import "./pages/burgerBreadcrumbs.css";

const HomePage = lazy(() => import("./HomePage"));
const PrivacyPolicy = lazy(() => import("./component/sections/legal/privacyPolicy"));
const TermsAndConditions = lazy(() => import("./component/sections/legal/termsConditions"));
const ArtistProfile = lazy(() => import("./component/sections/artistProfile/ArtistProfile"));
const ArtistApplication = lazy(() => import("./component/sections/artistApplication/ArtistApplication"));
const ArtistRegister = lazy(() => import("./component/sections/artistRegister/ArtistRegister"));
const ArtistDashboard = lazy(() => import("./component/sections/artistDashboard/ArtistDashboard"));
const BrandApplication = lazy(() => import("./component/sections/brandApplication/BrandApplication"));
const BrandDashboard = lazy(() => import("./component/sections/brandDashboard/BrandDashboard"));
const Dashboard = lazy(() => import("./component/sections/dashboard/Dashboard"));
const Login = lazy(() => import("./component/sections/login/Login"));
const EmblemDeepLink = lazy(() => import("./component/sections/emblem/EmblemDeepLink"));

const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const LuxembourgPassport = lazy(() => import("./pages/LuxembourgPassport"));
const LuxembourgPassportArtists = lazy(() => import("./pages/LuxembourgPassportArtists"));
const LuxembourgPassportEmblems = lazy(() => import("./pages/LuxembourgPassportEmblems"));
const ArtistDetailPage = lazy(() => import("./pages/ArtistDetailPage"));
const EmblemDetailPage = lazy(() => import("./pages/EmblemDetailPage"));
const UnPlusEmblems = lazy(() => import("./pages/UnPlusEmblems"));
const UnPlusTopLocalFood = lazy(() => import("./pages/UnPlusTopLocalFood"));
const UnPlusTopLocalMusic = lazy(() => import("./pages/UnPlusTopLocalMusic"));
const UnPlusPassports = lazy(() => import("./pages/UnPlusPassports"));
const UnPlusMusicItemPage = lazy(() => import("./pages/UnPlusMusicItemPage"));
const UnPlusEmblemsCountry = lazy(() => import("./pages/UnPlusEmblemsCountry"));
const UnPlusEmblemsCountryItem = lazy(() => import("./pages/UnPlusEmblemsCountryItem"));
const Partners = lazy(() => import("./pages/Partners"));
const ArtistsPage = lazy(() => import("./pages/ArtistsPage"));
const Press = lazy(() => import("./pages/Press"));
const News = lazy(() => import("./pages/News"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const FAQ = lazy(() => import("./pages/FAQ"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CreateYourPassport = lazy(() => import("./pages/CreateYourPassport"));
const WhatIsEmblem = lazy(() => import("./pages/WhatIsEmblem"));
const OurStory = lazy(() => import("./pages/OurStory"));
// WhatIsADigitalSouvenir page consolidated into /digital-souvenirs (2026-04).
// The /what-is-a-digital-souvenir URL now 301-redirects via firebase.json.
const Marketplace = lazy(() => import("./pages/Marketplace"));
const PassportDetail = lazy(() => import("./pages/PassportDetail"));
const PassportLocationDetail = lazy(() => import("./pages/PassportLocationDetail"));
const PassportEmblemDetail = lazy(() => import("./pages/PassportEmblemDetail"));
const EmblemDetail = lazy(() => import("./pages/EmblemDetail"));

const DigitalSouvenirs = lazy(() => import("./pages/seo/DigitalSouvenirs"));
const LuxembourgSouvenirs = lazy(() => import("./pages/seo/LuxembourgSouvenirs"));
const GPSVerifiedSouvenirs = lazy(() => import("./pages/seo/GPSVerifiedSouvenirs"));
const CulturalPassportApp = lazy(() => import("./pages/seo/CulturalPassportApp"));
const ForTourismPartners = lazy(() => import("./pages/seo/ForTourismPartners"));
const TravelPassportApp = lazy(() => import("./pages/seo/TravelPassportApp"));
const MuseumDigitalSouvenir = lazy(() => import("./pages/seo/MuseumDigitalSouvenir"));
const DigitalTravelStamps = lazy(() => import("./pages/seo/DigitalTravelStamps"));
const SelfGuidedTours = lazy(() => import("./pages/seo/SelfGuidedTours"));
const LocalizedDigitalSouvenirs = lazy(() => import("./pages/seo/LocalizedDigitalSouvenirs"));
const LocalizedInformationalPage = lazy(() => import("./pages/LocalizedInformationalPage"));
const LocalizedSeoLandingPage = lazy(() => import("./pages/seo/LocalizedSeoLandingPage"));

const AdminApp = lazy(loadWithChunkRecovery(() => import("./admin/AdminApp"), "admin:app"));

const SCROLL_STORAGE_KEY = "emblem_scroll_positions_v1";
const EMBLEM_WORLD_CATEGORY_KINDS = ["food", "drink", "dessert", "music"];
const buildLocalizedEmblemWorldPassportPath = (languageCode = "") =>
  `${languageCode ? `/${languageCode}` : ""}/emblem-world/passports`;

const LegacyPassportIndexRedirect = ({ languageCode = "" }) => (
  <Navigate replace to={buildLocalizedEmblemWorldPassportPath(languageCode)} />
);

const LegacyPassportDetailRedirect = ({
  languageCode = "",
  includeLocation = false,
  includeEmblem = false,
}) => {
  const { passportSlug, locationSlug, emblemSlug } = useParams();
  const basePath = buildLocalizedEmblemWorldPassportPath(languageCode);
  if (!passportSlug) {
    return <Navigate replace to={basePath} />;
  }
  const locationPath = includeLocation && locationSlug ? `/locations/${locationSlug}` : "";
  const emblemPath = includeEmblem && emblemSlug ? `/emblems/${emblemSlug}` : "";
  return <Navigate replace to={`${basePath}/${passportSlug}${locationPath}${emblemPath}`} />;
};

const CANONICAL_ROUTE_ALIASES = new Map([
  ["/home", "/"],
  ["/home/", "/"],
  ["/cookies", "/privacy/"],
  ["/cookies/", "/privacy/"],
]);
const QUERY_PARAM_ALLOWLIST = [
  {
    pattern: /^\/artist\/welcome\/?$/i,
    params: new Set(["token", "email", "name"]),
  },
];

const normalizeCanonicalPathname = (pathname) => {
  if (typeof pathname !== "string" || pathname.trim() === "" || pathname === "/") {
    return "/";
  }

  const trimmed = pathname.trim();
  const aliasedPath = CANONICAL_ROUTE_ALIASES.get(trimmed);
  if (aliasedPath) {
    return aliasedPath;
  }

  if (/\.[a-z0-9]+$/i.test(trimmed)) {
    return trimmed;
  }

  return trimmed.endsWith("/") ? trimmed : `${trimmed}/`;
};

const resolveAllowedQueryParams = (pathname) => {
  const normalizedPathname = normalizeCanonicalPathname(pathname);
  const matchedRule = QUERY_PARAM_ALLOWLIST.find(({ pattern }) =>
    pattern.test(normalizedPathname),
  );

  return matchedRule ? matchedRule.params : new Set();
};

const normalizeCanonicalSearch = (pathname, search) => {
  if (typeof search !== "string" || search.trim() === "" || search === "?") {
    return "";
  }

  const allowedParams = resolveAllowedQueryParams(pathname);
  const currentParams = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);

  if (!currentParams.toString()) {
    return "";
  }

  const nextParams = new URLSearchParams();
  for (const [key, value] of currentParams.entries()) {
    if (allowedParams.has(key) && String(value || "").trim() !== "") {
      nextParams.append(key, value);
    }
  }

  const serialized = nextParams.toString();
  return serialized ? `?${serialized}` : "";
};

const readScrollPositions = () => {
  try {
    const raw = window.sessionStorage.getItem(SCROLL_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    return {};
  }
};

const writeScrollPositions = (positions) => {
  try {
    window.sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions));
  } catch (_) {
    // Ignore storage failures in private browsing or constrained contexts.
  }
};

const getPathKey = (location) => `${location.pathname}${location.search}`;

const SCROLL_RESTORE_RETRY_DELAYS_MS = [
  80,
  180,
  360,
  650,
  1000,
  1500,
  2200,
  3200,
  4600,
  6200,
];
const SCROLL_RESTORE_MAX_DURATION_MS = 8000;
const SCROLL_RESTORE_TOLERANCE_PX = 5;
const SCROLL_RESTORE_CANCEL_KEYS = new Set([
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "End",
  "Home",
  "PageDown",
  "PageUp",
  " ",
]);

const clearScheduledScrollRestores = (scheduledRestores) => {
  scheduledRestores.forEach((handle) => {
    if (handle.timeoutId !== null) {
      window.clearTimeout(handle.timeoutId);
    }
    if (handle.frameId !== null) {
      window.cancelAnimationFrame(handle.frameId);
    }
  });
  scheduledRestores.length = 0;
};

const scheduleScrollRestore = (scheduledRestores, callback, delayMs = 0) => {
  const handle = {
    timeoutId: null,
    frameId: null,
  };

  const restore = () => {
    handle.timeoutId = null;
    handle.frameId = window.requestAnimationFrame(() => {
      handle.frameId = null;
      callback();
    });
  };

  if (delayMs > 0) {
    handle.timeoutId = window.setTimeout(restore, delayMs);
  } else {
    restore();
  }

  scheduledRestores.push(handle);
};

const getDocumentScrollHeight = () =>
  Math.max(
    document.body?.scrollHeight || 0,
    document.documentElement?.scrollHeight || 0,
  );

const getMaxScrollY = () =>
  Math.max(0, getDocumentScrollHeight() - window.innerHeight);

const isNearScrollPosition = (targetX, targetY) =>
  Math.abs(window.scrollX - targetX) <= SCROLL_RESTORE_TOLERANCE_PX &&
  Math.abs(window.scrollY - targetY) <= SCROLL_RESTORE_TOLERANCE_PX;

const listenForScrollRestoreInterruption = (onInterrupt) => {
  let didInterrupt = false;

  const interrupt = () => {
    if (didInterrupt) {
      return;
    }
    didInterrupt = true;
    onInterrupt();
  };

  const handleKeyDown = (event) => {
    if (SCROLL_RESTORE_CANCEL_KEYS.has(event.key)) {
      interrupt();
    }
  };

  const passiveOptions = { passive: true };
  window.addEventListener("wheel", interrupt, passiveOptions);
  window.addEventListener("touchstart", interrupt, passiveOptions);
  window.addEventListener("touchmove", interrupt, passiveOptions);
  window.addEventListener("pointerdown", interrupt, passiveOptions);
  window.addEventListener("keydown", handleKeyDown);

  return () => {
    window.removeEventListener("wheel", interrupt, passiveOptions);
    window.removeEventListener("touchstart", interrupt, passiveOptions);
    window.removeEventListener("touchmove", interrupt, passiveOptions);
    window.removeEventListener("pointerdown", interrupt, passiveOptions);
    window.removeEventListener("keydown", handleKeyDown);
  };
};

const saveScrollPosition = (location, x = window.scrollX, y = window.scrollY) => {
  if (typeof window === "undefined") {
    return;
  }

  const positions = readScrollPositions();
  const position = { x, y };
  const pathKey = getPathKey(location);

  positions[pathKey] = position;
  if (location.key) {
    positions[location.key] = position;
  }

  writeScrollPositions(positions);
};

function RouteEffects() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const restoreFrameRef = useRef([]);
  const isRestoringScrollRef = useRef(false);
  const locationKey = location.key;
  const locationPathname = location.pathname;
  const locationSearch = location.search;
  const locationHash = location.hash;
  const canonicalPathname = normalizeCanonicalPathname(locationPathname);
  const isCanonicalLocation = canonicalPathname === locationPathname;
  const canonicalSearch = normalizeCanonicalSearch(canonicalPathname, locationSearch);

  useEffect(() => {
    if (isCanonicalLocation) {
      return;
    }

    navigate(
      {
        pathname: canonicalPathname,
        search: locationSearch,
        hash: locationHash,
      },
      { replace: true },
    );
  }, [
    canonicalPathname,
    isCanonicalLocation,
    locationHash,
    locationSearch,
    navigate,
  ]);

  useEffect(() => {
    // Defer GA/gtag bootstrap to idle time. Lighthouse flagged
    // gtag.js as one of the top "unused JavaScript" contributors on
    // first paint (~60 KiB unused, blocks ~120 ms of main-thread)
    // because page-view and store-click events don't need to fire
    // until after the hero has rendered. Page-view tracking happens
    // on every navigation via the route effect below; the deferral
    // only delays the analytics handshake on the very first visit.
    let idleHandle;
    let timeoutHandle;
    const start = () => {
      initAnalytics();
      bindStoreLinkTracking();
    };
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(start, { timeout: 4000 });
    } else {
      timeoutHandle = window.setTimeout(start, 2500);
    }
    return () => {
      if (idleHandle != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle != null) {
        window.clearTimeout(timeoutHandle);
      }
    };
  }, []);

  useEffect(() => {
    if (!window.history || !("scrollRestoration" in window.history)) {
      return undefined;
    }

    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = "auto";
    };
  }, []);

  useLayoutEffect(() => {
    if (!isCanonicalLocation) {
      return undefined;
    }

    let ticking = false;
    const locationSnapshot = {
      key: locationKey,
      pathname: locationPathname,
      search: locationSearch,
    };

    const persist = () => {
      saveScrollPosition(locationSnapshot);
    };

    const handleScroll = () => {
      if (isRestoringScrollRef.current) {
        return;
      }

      if (ticking) {
        return;
      }
      ticking = true;
      window.requestAnimationFrame(() => {
        persist();
        ticking = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("beforeunload", persist);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("beforeunload", persist);
      if (!isRestoringScrollRef.current) {
        persist();
      }
    };
  }, [isCanonicalLocation, locationKey, locationPathname, locationSearch]);

  useLayoutEffect(() => {
    if (!isCanonicalLocation) {
      return undefined;
    }

    const locationSnapshot = {
      key: locationKey,
      pathname: locationPathname,
      search: locationSearch,
    };
    const scheduledRestores = restoreFrameRef.current;

    clearScheduledScrollRestores(scheduledRestores);

    const positions = readScrollPositions();
    const storedPosition = positions[locationKey] || positions[getPathKey(locationSnapshot)];
    let removeInterruptionListener = null;
    let resizeObserver = null;

    if (navigationType === "POP" && storedPosition) {
      const targetX = storedPosition.x || 0;
      const targetY = storedPosition.y || 0;
      let wasInterrupted = false;
      let didFinishRestoring = false;

      const cleanupRestoreWatchers = () => {
        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }
        if (removeInterruptionListener) {
          removeInterruptionListener();
          removeInterruptionListener = null;
        }
      };

      const finishRestoring = () => {
        if (didFinishRestoring) {
          return;
        }
        didFinishRestoring = true;
        isRestoringScrollRef.current = false;
        cleanupRestoreWatchers();
        clearScheduledScrollRestores(scheduledRestores);
        saveScrollPosition(locationSnapshot);
      };

      const restoreToStoredPosition = () => {
        if (wasInterrupted || didFinishRestoring) {
          return;
        }

        window.scrollTo(targetX, targetY);

        if (isNearScrollPosition(targetX, targetY)) {
          finishRestoring();
        }
      };

      const cancelPendingRestores = () => {
        wasInterrupted = true;
        isRestoringScrollRef.current = false;
        clearScheduledScrollRestores(scheduledRestores);
        cleanupRestoreWatchers();
        saveScrollPosition(locationSnapshot);
      };

      isRestoringScrollRef.current = true;
      removeInterruptionListener = listenForScrollRestoreInterruption(cancelPendingRestores);
      restoreToStoredPosition();

      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => {
          if (!wasInterrupted && !didFinishRestoring && getMaxScrollY() >= targetY) {
            scheduleScrollRestore(scheduledRestores, restoreToStoredPosition, 0);
          }
        });
        if (document.documentElement) {
          resizeObserver.observe(document.documentElement);
        }
        if (document.body) {
          resizeObserver.observe(document.body);
        }
      }

      SCROLL_RESTORE_RETRY_DELAYS_MS.forEach((delayMs) => {
        scheduleScrollRestore(
          scheduledRestores,
          restoreToStoredPosition,
          delayMs,
        );
      });
      scheduleScrollRestore(scheduledRestores, finishRestoring, SCROLL_RESTORE_MAX_DURATION_MS);
    } else if (navigationType !== "POP") {
      isRestoringScrollRef.current = false;
      window.scrollTo(0, 0);
    }

    return () => {
      isRestoringScrollRef.current = false;
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (removeInterruptionListener) {
        removeInterruptionListener();
      }
      clearScheduledScrollRestores(scheduledRestores);
    };
  }, [isCanonicalLocation, locationKey, locationPathname, locationSearch, navigationType]);

  useEffect(() => {
    if (!isCanonicalLocation) {
      return;
    }

    captureAttributionFromUrl({
      pathname: locationPathname,
      search: locationSearch,
    });
  }, [isCanonicalLocation, locationPathname, locationSearch]);

  useEffect(() => {
    if (!isCanonicalLocation || canonicalSearch === locationSearch) {
      return;
    }

    navigate(
      {
        pathname: canonicalPathname,
        search: canonicalSearch,
        hash: locationHash,
      },
      { replace: true },
    );
  }, [
    canonicalPathname,
    canonicalSearch,
    isCanonicalLocation,
    locationHash,
    locationSearch,
    navigate,
  ]);

  useEffect(() => {
    if (!isCanonicalLocation || canonicalSearch !== locationSearch) {
      return;
    }

    const path = `${locationPathname}${locationSearch}`;
    trackPageView(path);
    decorateStoreLinks(locationPathname);
  }, [
    canonicalSearch,
    isCanonicalLocation,
    locationPathname,
    locationSearch,
  ]);

  return null;
}

function RouteScopedComponent({ Component }) {
  const location = useLocation();
  return <Component key={`${location.pathname}${location.search}`} />;
}

// Keeps LanguageContext aligned with the URL on every navigation.
// When a visitor lands on or navigates to /es/contact, the language
// becomes 'es'; when they navigate back to /contact, it becomes 'en'.
// Without this, switching pages inside a /<lang>/... cluster would
// leave the language stale on first paint after route change.
function LanguageURLBridge() {
  const location = useLocation();
  const ctx = React.useContext(LanguageContext);
  const setLanguage = ctx?.setLanguage;

  React.useEffect(() => {
    if (!setLanguage) return;
    const urlLanguage = inferSiteLanguageCode(location.pathname);
    if (urlLanguage && ctx?.language !== urlLanguage) {
      setLanguage(urlLanguage);
    }
  }, [location.pathname, setLanguage, ctx?.language]);

  return null;
}

const loadingFallback = (
  <div className="app-static-boot" role="status" aria-live="polite" aria-label="Emblem app loading">
    <div className="app-static-boot__halo" aria-hidden="true" />
    <div className="app-static-boot__logo-wrap" aria-hidden="true">
      <img className="app-static-boot__logo" src="/fullblack-2.png" alt="" />
    </div>
    <p className="app-static-boot__eyebrow">Emblem: Digital Souvenirs</p>
    {/* Crawler-visible fallback. Replaces the previous "Loading
        Emblem" placeholder so search snippets describe what the
        product is, not the spinner. */}
    <p className="app-static-boot__sr-only">
      Emblem is a cultural experience app for collecting digital souvenirs from real-world culture. Explore cultural passports, meaningful places, local discoveries, and digital memories through the Emblem app.
    </p>
    <div className="app-static-boot__bar" aria-hidden="true">
      <span />
    </div>
  </div>
);

export default function App() {
  return (
    <div>
      <LanguageProvider>
        <BrowserRouter>
          <RouteEffects />
          <LanguageURLBridge />
          <Suspense fallback={loadingFallback}>
            <Routes>
              <Route index element={<HomePage />} />
              <Route path="/home" element={<HomePage />} />
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`home-root-${languageCode}`}
                  path={`/${languageCode}`}
                  element={<HomePage />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`home-${languageCode}`}
                  path={`/${languageCode}/home`}
                  element={<HomePage />}
                />
              ))}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/cookies" element={<PrivacyPolicy />} />

              <Route path="/our-story" element={<OurStory />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/luxembourg-passport-2026" element={<LuxembourgPassport />} />
              <Route
                path="/luxembourg-passport-2026/artists"
                element={<LuxembourgPassportArtists />}
              />
              <Route
                path="/luxembourg-passport-2026/emblems"
                element={<LuxembourgPassportEmblems />}
              />
              <Route
                path="/luxembourg-passport-2026/artists/:artistSlug/:emblemSlug"
                element={<RouteScopedComponent Component={EmblemDetailPage} />}
              />
              <Route
                path="/luxembourg-passport-2026/artists/:artistSlug"
                element={<RouteScopedComponent Component={ArtistDetailPage} />}
              />
              <Route
                path="/luxembourg-passport-2026/artists/:artistId/emblems/:emblemId"
                element={<RouteScopedComponent Component={EmblemDetailPage} />}
              />
              <Route
                path="/luxembourg-passport-2026/artists/:artistId/:artistLegacySlug/emblems/:emblemId/:emblemLegacySlug"
                element={<RouteScopedComponent Component={EmblemDetailPage} />}
              />
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`luxembourg-passport-${languageCode}`}
                  path={`/${languageCode}/luxembourg-passport-2026`}
                  element={<LuxembourgPassport />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`luxembourg-passport-artists-${languageCode}`}
                  path={`/${languageCode}/luxembourg-passport-2026/artists`}
                  element={<LuxembourgPassportArtists />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`luxembourg-passport-emblems-${languageCode}`}
                  path={`/${languageCode}/luxembourg-passport-2026/emblems`}
                  element={<LuxembourgPassportEmblems />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`luxembourg-passport-artist-detail-${languageCode}`}
                  path={`/${languageCode}/luxembourg-passport-2026/artists/:artistSlug`}
                  element={<RouteScopedComponent Component={ArtistDetailPage} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`luxembourg-passport-emblem-detail-slug-${languageCode}`}
                  path={`/${languageCode}/luxembourg-passport-2026/artists/:artistSlug/:emblemSlug`}
                  element={<RouteScopedComponent Component={EmblemDetailPage} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`luxembourg-passport-emblem-detail-id-${languageCode}`}
                  path={`/${languageCode}/luxembourg-passport-2026/artists/:artistId/emblems/:emblemId`}
                  element={<RouteScopedComponent Component={EmblemDetailPage} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`luxembourg-passport-emblem-detail-legacy-${languageCode}`}
                  path={`/${languageCode}/luxembourg-passport-2026/artists/:artistId/:artistLegacySlug/emblems/:emblemId/:emblemLegacySlug`}
                  element={<RouteScopedComponent Component={EmblemDetailPage} />}
                />
              ))}
              {/* New canonical Emblem World routes — every page mounts on
                  both `/emblem-world/...` (canonical) and the legacy
                  `/un-plus-emblems/...` paths below so existing inbound
                  links don't 404 while search engines re-index. */}
              <Route path="/emblem-world" element={<UnPlusEmblems />} />
              <Route path="/emblem-world/top-local-food" element={<UnPlusTopLocalFood />} />
              <Route path="/emblem-world/top-local-music" element={<UnPlusTopLocalMusic />} />
              <Route path="/emblem-world/passports" element={<UnPlusPassports />} />
              <Route
                path="/emblem-world/passports/:passportSlug"
                element={<RouteScopedComponent Component={PassportDetail} />}
              />
              <Route
                path="/emblem-world/passports/:passportSlug/locations/:locationSlug"
                element={<RouteScopedComponent Component={PassportLocationDetail} />}
              />
              <Route
                path="/emblem-world/passports/:passportSlug/locations/:locationSlug/emblems/:emblemSlug"
                element={<RouteScopedComponent Component={PassportEmblemDetail} />}
              />
              <Route
                path="/emblem-world/passports/:passportSlug/emblems/:emblemSlug"
                element={<RouteScopedComponent Component={PassportEmblemDetail} />}
              />
              <Route
                path="/emblem-world/:countrySlug/music/:itemSlug"
                element={<RouteScopedComponent Component={UnPlusMusicItemPage} />}
              />
              <Route
                path="/emblem-world/:countrySlug/:territorySlug/music/:itemSlug"
                element={<RouteScopedComponent Component={UnPlusMusicItemPage} />}
              />
              {EMBLEM_WORLD_CATEGORY_KINDS.map((kind) => (
                <Route
                  key={`emblem-world-country-category-${kind}`}
                  path={`/emblem-world/:countrySlug/${kind}`}
                  element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
                />
              ))}
              {EMBLEM_WORLD_CATEGORY_KINDS.map((kind) => (
                <Route
                  key={`emblem-world-territory-category-${kind}`}
                  path={`/emblem-world/:countrySlug/:territorySlug/${kind}`}
                  element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
                />
              ))}
              <Route
                path="/emblem-world/:countrySlug"
                element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
              />
              <Route
                path="/emblem-world/:countrySlug/:territorySlug"
                element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
              />
              <Route
                path="/emblem-world/:countrySlug/:itemKind/:itemSlug"
                element={<RouteScopedComponent Component={UnPlusEmblemsCountryItem} />}
              />
              <Route
                path="/emblem-world/:countrySlug/:territorySlug/:itemKind/:itemSlug"
                element={<RouteScopedComponent Component={UnPlusEmblemsCountryItem} />}
              />
              {/* Legacy UN+ Emblems routes — same components, kept so deep
                  links from external sources still resolve. Each page sets
                  its canonical to the new /emblem-world/* URL. */}
              <Route path="/un-plus-emblems" element={<UnPlusEmblems />} />
              <Route path="/un-plus-emblems/top-local-food" element={<UnPlusTopLocalFood />} />
              <Route path="/un-plus-emblems/top-local-music" element={<UnPlusTopLocalMusic />} />
              <Route path="/un-plus-emblems/passports" element={<LegacyPassportIndexRedirect />} />
              <Route
                path="/un-plus-emblems/passports/:passportSlug"
                element={<LegacyPassportDetailRedirect />}
              />
              <Route
                path="/un-plus-emblems/passports/:passportSlug/locations/:locationSlug"
                element={<LegacyPassportDetailRedirect includeLocation />}
              />
              <Route
                path="/un-plus-emblems/passports/:passportSlug/locations/:locationSlug/emblems/:emblemSlug"
                element={<LegacyPassportDetailRedirect includeLocation includeEmblem />}
              />
              <Route
                path="/un-plus-emblems/passports/:passportSlug/emblems/:emblemSlug"
                element={<LegacyPassportDetailRedirect includeEmblem />}
              />
              <Route
                path="/un-plus-emblems/:countrySlug/music/:itemSlug"
                element={<RouteScopedComponent Component={UnPlusMusicItemPage} />}
              />
              <Route
                path="/un-plus-emblems/:countrySlug/:territorySlug/music/:itemSlug"
                element={<RouteScopedComponent Component={UnPlusMusicItemPage} />}
              />
              {EMBLEM_WORLD_CATEGORY_KINDS.map((kind) => (
                <Route
                  key={`un-plus-emblems-country-category-${kind}`}
                  path={`/un-plus-emblems/:countrySlug/${kind}`}
                  element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
                />
              ))}
              {EMBLEM_WORLD_CATEGORY_KINDS.map((kind) => (
                <Route
                  key={`un-plus-emblems-territory-category-${kind}`}
                  path={`/un-plus-emblems/:countrySlug/:territorySlug/${kind}`}
                  element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
                />
              ))}
              <Route
                path="/un-plus-emblems/:countrySlug"
                element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
              />
              <Route
                path="/un-plus-emblems/:countrySlug/:territorySlug"
                element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
              />
              <Route
                path="/un-plus-emblems/:countrySlug/:itemKind/:itemSlug"
                element={<RouteScopedComponent Component={UnPlusEmblemsCountryItem} />}
              />
              <Route
                path="/un-plus-emblems/:countrySlug/:territorySlug/:itemKind/:itemSlug"
                element={<RouteScopedComponent Component={UnPlusEmblemsCountryItem} />}
              />
              {/* Localized Emblem World routes (new canonical) */}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-world-${languageCode}`}
                  path={`/${languageCode}/emblem-world`}
                  element={<UnPlusEmblems />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-world-top-local-food-${languageCode}`}
                  path={`/${languageCode}/emblem-world/top-local-food`}
                  element={<UnPlusTopLocalFood />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-world-top-local-music-${languageCode}`}
                  path={`/${languageCode}/emblem-world/top-local-music`}
                  element={<UnPlusTopLocalMusic />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-world-passports-${languageCode}`}
                  path={`/${languageCode}/emblem-world/passports`}
                  element={<UnPlusPassports />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-world-passport-detail-${languageCode}`}
                  path={`/${languageCode}/emblem-world/passports/:passportSlug`}
                  element={<RouteScopedComponent Component={PassportDetail} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-world-passport-location-detail-${languageCode}`}
                  path={`/${languageCode}/emblem-world/passports/:passportSlug/locations/:locationSlug`}
                  element={<RouteScopedComponent Component={PassportLocationDetail} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-world-passport-location-emblem-detail-${languageCode}`}
                  path={`/${languageCode}/emblem-world/passports/:passportSlug/locations/:locationSlug/emblems/:emblemSlug`}
                  element={<RouteScopedComponent Component={PassportEmblemDetail} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-world-passport-emblem-detail-${languageCode}`}
                  path={`/${languageCode}/emblem-world/passports/:passportSlug/emblems/:emblemSlug`}
                  element={<RouteScopedComponent Component={PassportEmblemDetail} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-world-music-item-${languageCode}`}
                  path={`/${languageCode}/emblem-world/:countrySlug/music/:itemSlug`}
                  element={<RouteScopedComponent Component={UnPlusMusicItemPage} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-world-territory-music-item-${languageCode}`}
                  path={`/${languageCode}/emblem-world/:countrySlug/:territorySlug/music/:itemSlug`}
                  element={<RouteScopedComponent Component={UnPlusMusicItemPage} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.flatMap((languageCode) =>
                EMBLEM_WORLD_CATEGORY_KINDS.map((kind) => (
                  <Route
                    key={`emblem-world-country-category-${languageCode}-${kind}`}
                    path={`/${languageCode}/emblem-world/:countrySlug/${kind}`}
                    element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
                  />
                )),
              )}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.flatMap((languageCode) =>
                EMBLEM_WORLD_CATEGORY_KINDS.map((kind) => (
                  <Route
                    key={`emblem-world-territory-category-${languageCode}-${kind}`}
                    path={`/${languageCode}/emblem-world/:countrySlug/:territorySlug/${kind}`}
                    element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
                  />
                )),
              )}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-world-country-${languageCode}`}
                  path={`/${languageCode}/emblem-world/:countrySlug`}
                  element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-world-country-territory-${languageCode}`}
                  path={`/${languageCode}/emblem-world/:countrySlug/:territorySlug`}
                  element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-world-country-item-${languageCode}`}
                  path={`/${languageCode}/emblem-world/:countrySlug/:itemKind/:itemSlug`}
                  element={<RouteScopedComponent Component={UnPlusEmblemsCountryItem} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-world-territory-item-${languageCode}`}
                  path={`/${languageCode}/emblem-world/:countrySlug/:territorySlug/:itemKind/:itemSlug`}
                  element={<RouteScopedComponent Component={UnPlusEmblemsCountryItem} />}
                />
              ))}
              {/* Legacy localized UN+ Emblems routes (kept for back-compat) */}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`un-plus-emblems-${languageCode}`}
                  path={`/${languageCode}/un-plus-emblems`}
                  element={<UnPlusEmblems />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`un-plus-emblems-top-local-food-${languageCode}`}
                  path={`/${languageCode}/un-plus-emblems/top-local-food`}
                  element={<UnPlusTopLocalFood />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`un-plus-emblems-top-local-music-${languageCode}`}
                  path={`/${languageCode}/un-plus-emblems/top-local-music`}
                  element={<UnPlusTopLocalMusic />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`un-plus-emblems-passports-${languageCode}`}
                  path={`/${languageCode}/un-plus-emblems/passports`}
                  element={<LegacyPassportIndexRedirect languageCode={languageCode} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`un-plus-emblems-passport-detail-${languageCode}`}
                  path={`/${languageCode}/un-plus-emblems/passports/:passportSlug`}
                  element={<LegacyPassportDetailRedirect languageCode={languageCode} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`un-plus-emblems-passport-location-detail-${languageCode}`}
                  path={`/${languageCode}/un-plus-emblems/passports/:passportSlug/locations/:locationSlug`}
                  element={<LegacyPassportDetailRedirect languageCode={languageCode} includeLocation />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`un-plus-emblems-passport-location-emblem-detail-${languageCode}`}
                  path={`/${languageCode}/un-plus-emblems/passports/:passportSlug/locations/:locationSlug/emblems/:emblemSlug`}
                  element={<LegacyPassportDetailRedirect languageCode={languageCode} includeLocation includeEmblem />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`un-plus-emblems-passport-emblem-detail-${languageCode}`}
                  path={`/${languageCode}/un-plus-emblems/passports/:passportSlug/emblems/:emblemSlug`}
                  element={<LegacyPassportDetailRedirect languageCode={languageCode} includeEmblem />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`un-plus-emblems-music-item-${languageCode}`}
                  path={`/${languageCode}/un-plus-emblems/:countrySlug/music/:itemSlug`}
                  element={<RouteScopedComponent Component={UnPlusMusicItemPage} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`un-plus-emblems-territory-music-item-${languageCode}`}
                  path={`/${languageCode}/un-plus-emblems/:countrySlug/:territorySlug/music/:itemSlug`}
                  element={<RouteScopedComponent Component={UnPlusMusicItemPage} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.flatMap((languageCode) =>
                EMBLEM_WORLD_CATEGORY_KINDS.map((kind) => (
                  <Route
                    key={`un-plus-emblems-country-category-${languageCode}-${kind}`}
                    path={`/${languageCode}/un-plus-emblems/:countrySlug/${kind}`}
                    element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
                  />
                )),
              )}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.flatMap((languageCode) =>
                EMBLEM_WORLD_CATEGORY_KINDS.map((kind) => (
                  <Route
                    key={`un-plus-emblems-territory-category-${languageCode}-${kind}`}
                    path={`/${languageCode}/un-plus-emblems/:countrySlug/:territorySlug/${kind}`}
                    element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
                  />
                )),
              )}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`un-plus-emblems-country-${languageCode}`}
                  path={`/${languageCode}/un-plus-emblems/:countrySlug`}
                  element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`un-plus-emblems-country-territory-${languageCode}`}
                  path={`/${languageCode}/un-plus-emblems/:countrySlug/:territorySlug`}
                  element={<RouteScopedComponent Component={UnPlusEmblemsCountry} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`un-plus-emblems-country-item-${languageCode}`}
                  path={`/${languageCode}/un-plus-emblems/:countrySlug/:itemKind/:itemSlug`}
                  element={<RouteScopedComponent Component={UnPlusEmblemsCountryItem} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`un-plus-emblems-territory-item-${languageCode}`}
                  path={`/${languageCode}/un-plus-emblems/:countrySlug/:territorySlug/:itemKind/:itemSlug`}
                  element={<RouteScopedComponent Component={UnPlusEmblemsCountryItem} />}
                />
              ))}
              <Route path="/partners" element={<Partners />} />
              <Route path="/create-your-passport" element={<CreateYourPassport />} />
              <Route path="/artists" element={<ArtistsPage />} />
              <Route path="/press" element={<Press />} />
              <Route path="/news" element={<News />} />
              <Route
                path="/news/:slug"
                element={<RouteScopedComponent Component={NewsArticle} />}
              />
              <Route path="/faq" element={<FAQ />} />
              {/* Per-language FAQ — render the full FAQ component (which
                  reads strings from src/locales/<lang>/faq.json), so
                  /es/faq, /fr/faq etc. show the full 29-Q accordion in
                  the right language. These are listed BEFORE the
                  LOCALIZED_INFORMATIONAL_ROUTE_DEFINITIONS block so
                  React Router picks them first. */}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`faq-${languageCode}`}
                  path={`/${languageCode}/faq`}
                  element={<FAQ />}
                />
              ))}
              <Route path="/contact" element={<ContactPage />} />
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`contact-${languageCode}`}
                  path={`/${languageCode}/contact`}
                  element={<ContactPage />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`press-${languageCode}`}
                  path={`/${languageCode}/press`}
                  element={<Press />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`news-${languageCode}`}
                  path={`/${languageCode}/news`}
                  element={<News />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`news-slug-${languageCode}`}
                  path={`/${languageCode}/news/:slug`}
                  element={<RouteScopedComponent Component={NewsArticle} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`how-it-works-${languageCode}`}
                  path={`/${languageCode}/how-it-works`}
                  element={<HowItWorks />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`our-story-${languageCode}`}
                  path={`/${languageCode}/our-story`}
                  element={<OurStory />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`partners-${languageCode}`}
                  path={`/${languageCode}/partners`}
                  element={<Partners />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`artists-${languageCode}`}
                  path={`/${languageCode}/artists`}
                  element={<ArtistsPage />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`what-is-an-emblem-${languageCode}`}
                  path={`/${languageCode}/what-is-an-emblem`}
                  element={<WhatIsEmblem />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`what-is-emblem-legacy-${languageCode}`}
                  path={`/${languageCode}/what-is-emblem`}
                  element={<Navigate replace to={`/${languageCode}/what-is-an-emblem`} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`login-${languageCode}`}
                  path={`/${languageCode}/login`}
                  element={<Login />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`join-${languageCode}`}
                  path={`/${languageCode}/join`}
                  element={<Login />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`privacy-${languageCode}`}
                  path={`/${languageCode}/privacy`}
                  element={<PrivacyPolicy />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`terms-${languageCode}`}
                  path={`/${languageCode}/terms`}
                  element={<TermsAndConditions />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`cookies-${languageCode}`}
                  path={`/${languageCode}/cookies`}
                  element={<PrivacyPolicy />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`artist-apply-${languageCode}`}
                  path={`/${languageCode}/artist/become-an-emblem-artist`}
                  element={<ArtistApplication />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`brand-apply-${languageCode}`}
                  path={`/${languageCode}/brand/partner-with-emblem`}
                  element={<BrandApplication />}
                />
              ))}
              <Route path="/what-is-an-emblem" element={<WhatIsEmblem />} />
              <Route path="/what-is-emblem" element={<Navigate replace to="/what-is-an-emblem" />} />
              {/* /what-is-a-digital-souvenir route removed in 2026-04
                  consolidation. Content lives at /digital-souvenirs. The URL
                  301-redirects via firebase.json (see hosting redirects). */}
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/passports" element={<LegacyPassportIndexRedirect />} />
              <Route path="/passports/all" element={<LegacyPassportIndexRedirect />} />
              <Route
                path="/passports/:passportSlug"
                element={<LegacyPassportDetailRedirect />}
              />
              <Route
                path="/passports/:passportSlug/locations/:locationSlug"
                element={<LegacyPassportDetailRedirect includeLocation />}
              />
              <Route
                path="/passports/:passportSlug/locations/:locationSlug/emblems/:emblemSlug"
                element={<LegacyPassportDetailRedirect includeLocation includeEmblem />}
              />
              <Route
                path="/passports/:passportSlug/emblems/:emblemSlug"
                element={<LegacyPassportDetailRedirect includeEmblem />}
              />
              <Route
                path="/passports/:institutionId/:passportId/:passportSlug"
                element={<LegacyPassportDetailRedirect />}
              />
              <Route
                path="/passports/:institutionId/:passportId/:passportSlug/locations/:locationSlug"
                element={<LegacyPassportDetailRedirect includeLocation />}
              />
              <Route
                path="/passports/:institutionId/:passportId/:passportSlug/locations/:locationSlug/emblems/:emblemSlug"
                element={<LegacyPassportDetailRedirect includeLocation includeEmblem />}
              />
              <Route
                path="/passports/:institutionId/:passportId/:passportSlug/emblems/:emblemId/:emblemSlug"
                element={<LegacyPassportDetailRedirect includeEmblem />}
              />
              <Route
                path="/passports/:institutionId/:passportId/:passportSlug/emblems/:emblemId"
                element={<LegacyPassportDetailRedirect includeEmblem />}
              />
              <Route
                path="/passports/:institutionId/:passportId"
                element={<RouteScopedComponent Component={PassportDetail} />}
              />
              <Route
                path="/emblem/:emblemId/:emblemSlug"
                element={<RouteScopedComponent Component={EmblemDetail} />}
              />
              <Route
                path="/emblem/:emblemId"
                element={<RouteScopedComponent Component={EmblemDetail} />}
              />
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`marketplace-${languageCode}`}
                  path={`/${languageCode}/marketplace`}
                  element={<Marketplace />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`passports-${languageCode}`}
                  path={`/${languageCode}/passports`}
                  element={<LegacyPassportIndexRedirect languageCode={languageCode} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`passports-all-${languageCode}`}
                  path={`/${languageCode}/passports/all`}
                  element={<LegacyPassportIndexRedirect languageCode={languageCode} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`passport-detail-slug-${languageCode}`}
                  path={`/${languageCode}/passports/:passportSlug`}
                  element={<LegacyPassportDetailRedirect languageCode={languageCode} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`passport-location-detail-slug-${languageCode}`}
                  path={`/${languageCode}/passports/:passportSlug/locations/:locationSlug`}
                  element={<LegacyPassportDetailRedirect languageCode={languageCode} includeLocation />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`passport-location-emblem-detail-slug-${languageCode}`}
                  path={`/${languageCode}/passports/:passportSlug/locations/:locationSlug/emblems/:emblemSlug`}
                  element={<LegacyPassportDetailRedirect languageCode={languageCode} includeLocation includeEmblem />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`passport-emblem-detail-slug-${languageCode}`}
                  path={`/${languageCode}/passports/:passportSlug/emblems/:emblemSlug`}
                  element={<LegacyPassportDetailRedirect languageCode={languageCode} includeEmblem />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`passport-detail-legacy-slug-${languageCode}`}
                  path={`/${languageCode}/passports/:institutionId/:passportId/:passportSlug`}
                  element={<LegacyPassportDetailRedirect languageCode={languageCode} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`passport-location-detail-legacy-slug-${languageCode}`}
                  path={`/${languageCode}/passports/:institutionId/:passportId/:passportSlug/locations/:locationSlug`}
                  element={<LegacyPassportDetailRedirect languageCode={languageCode} includeLocation />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`passport-location-emblem-detail-legacy-slug-${languageCode}`}
                  path={`/${languageCode}/passports/:institutionId/:passportId/:passportSlug/locations/:locationSlug/emblems/:emblemSlug`}
                  element={<LegacyPassportDetailRedirect languageCode={languageCode} includeLocation includeEmblem />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`passport-emblem-detail-legacy-slug-${languageCode}`}
                  path={`/${languageCode}/passports/:institutionId/:passportId/:passportSlug/emblems/:emblemId/:emblemSlug`}
                  element={<LegacyPassportDetailRedirect languageCode={languageCode} includeEmblem />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`passport-emblem-detail-${languageCode}`}
                  path={`/${languageCode}/passports/:institutionId/:passportId/:passportSlug/emblems/:emblemId`}
                  element={<LegacyPassportDetailRedirect languageCode={languageCode} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`passport-detail-${languageCode}`}
                  path={`/${languageCode}/passports/:institutionId/:passportId`}
                  element={<RouteScopedComponent Component={PassportDetail} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-detail-slug-${languageCode}`}
                  path={`/${languageCode}/emblem/:emblemId/:emblemSlug`}
                  element={<RouteScopedComponent Component={EmblemDetail} />}
                />
              ))}
              {NON_DEFAULT_SITE_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`emblem-detail-${languageCode}`}
                  path={`/${languageCode}/emblem/:emblemId`}
                  element={<RouteScopedComponent Component={EmblemDetail} />}
                />
              ))}

              <Route path="/digital-souvenirs" element={<DigitalSouvenirs />} />
              {DIGITAL_SOUVENIR_NON_DEFAULT_LANGUAGE_CODES.map((languageCode) => (
                <Route
                  key={`digital-souvenirs-${languageCode}`}
                  path={`/${languageCode}/digital-souvenirs`}
                  element={<LocalizedDigitalSouvenirs languageCode={languageCode} />}
                />
              ))}
              {LOCALIZED_SEO_LANDING_ROUTE_DEFINITIONS.flatMap((page) =>
                page.nonDefaultLanguageCodes.map((languageCode) => (
                  <Route
                    key={`${page.key}-${languageCode}`}
                    path={`/${languageCode}/${page.key}`}
                    element={
                      <LocalizedSeoLandingPage
                        pageKey={page.key}
                        languageCode={languageCode}
                      />
                    }
                  />
                ))
              )}
              {LOCALIZED_INFORMATIONAL_ROUTE_DEFINITIONS.flatMap((page) =>
                page.nonDefaultLanguageCodes.map((languageCode) => (
                  <Route
                    key={`${page.key}-info-${languageCode}`}
                    path={`/${languageCode}/${page.key}`}
                    element={
                      <LocalizedInformationalPage
                        pageKey={page.key}
                        languageCode={languageCode}
                      />
                    }
                  />
                ))
              )}
              <Route
                path="/emblem-app"
                element={<Navigate to="/" replace />}
              />
              <Route path="/digital-souvenirs-luxembourg" element={<LuxembourgSouvenirs />} />
              <Route
                path="/luxembourg-souvenirs"
                element={<Navigate to="/digital-souvenirs-luxembourg" replace />}
              />
              <Route
                path="/luxembourg"
                element={<Navigate to="/digital-souvenirs-luxembourg" replace />}
              />
              <Route path="/gps-verified-souvenirs" element={<GPSVerifiedSouvenirs />} />
              <Route path="/cultural-passport-app" element={<CulturalPassportApp />} />
              <Route path="/for-tourism-partners" element={<ForTourismPartners />} />
              <Route path="/travel-passport-app" element={<TravelPassportApp />} />
              <Route path="/museum-digital-souvenir" element={<MuseumDigitalSouvenir />} />
              <Route path="/digital-travel-stamps" element={<DigitalTravelStamps />} />
              <Route path="/self-guided-tours" element={<SelfGuidedTours />} />

              <Route path="/login" element={<Login />} />
              <Route path="/join" element={<Login />} />

              <Route
                path="/artist/become-an-emblem-artist"
                element={<ArtistApplication />}
              />
              <Route path="/artist/welcome" element={<ArtistRegister />} />
              <Route path="/artist/dashboard" element={<ArtistDashboard />} />
              <Route path="/artist" element={<Login />} />
              <Route path="/artist/:artistId" element={<ArtistProfile />} />

              <Route
                path="/brand/partner-with-emblem"
                element={<BrandApplication />}
              />
              <Route path="/brand/dashboard" element={<BrandDashboard />} />
              <Route path="/brand" element={<Login />} />

              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/e/:emblemId" element={<EmblemDeepLink />} />
              <Route path="/p/:postOwnerId/:postId" element={<EmblemDeepLink />} />

              <Route path="/admin/*" element={<AdminApp />} />
            </Routes>
          </Suspense>
          <CookieConsentBanner />
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}
